import { DownloadOutlined } from '@ant-design/icons';
import { Alert, App, AutoComplete, Button, Form, Input, Modal, Select } from 'antd';
import { useEffect, useState } from 'react';
import { PickOption, searchUserOptions } from 'components/add-members-modal';
import DebounceSelect from 'components/debounce-select/select';
import { linkTicket, revokeTicket, updateTicket } from '../api';
import { GalaTemplate, GalaTicketRow, errorText, groupFilter } from '../contract';
import { fileUrl } from '../lib/file-url';
import styles from '../styles.module.scss';

const { TextArea } = Input;

/** Khoá ảnh vé để hiện: ảnh WebP nhẹ, rồi PNG đã vẽ, rồi ảnh làm sẵn của khách. */
export const ticketThumbKey = (t: GalaTicketRow) =>
  t.renderedWebpKey || t.renderedImageKey || t.customImageKey || null;

/** Tên trên vé hoặc @username, để gọi người trong tiêu đề modal. */
const ticketLabel = (t: GalaTicketRow) => t.displayName || (t.user ? `@${t.user.username}` : `#${t.id}`);

// ---------------------------------------------------------------------------
// Sửa vé
// ---------------------------------------------------------------------------

interface EditProps {
  eventId: number;
  ticket: GalaTicketRow | null;
  templates: GalaTemplate[];
  groups: string[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface EditValues {
  displayName: string;
  guestGroup: string;
  note: string;
  templateId: number | null;
}

/** Sửa tên trên vé / nhóm / ghi chú / mẫu. Đổi tên hoặc mẫu thì server vẽ lại vé. */
export const EditTicketModal = ({ eventId, ticket, templates, groups, onClose, onSaved }: EditProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<EditValues>();
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!ticket) return;
    const v = await form.validateFields();
    setSaving(true);
    try {
      await updateTicket(eventId, ticket.id, {
        displayName: v.displayName.trim(),
        guestGroup: v.guestGroup?.trim() ? v.guestGroup.trim().toUpperCase() : null,
        note: v.note?.trim() ? v.note.trim() : null,
        templateId: v.templateId ?? null,
      });
      message.success('Đã lưu vé.');
      await onSaved();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không lưu được vé.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={ticket ? `Sửa vé — ${ticketLabel(ticket)}` : 'Sửa vé'}
      open={!!ticket}
      onCancel={onClose}
      onOk={submit}
      okText="Lưu"
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnClose
    >
      <Form
        key={ticket?.id}
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          displayName: ticket?.displayName ?? '',
          guestGroup: ticket?.guestGroup ?? '',
          note: ticket?.note ?? '',
          templateId: ticket?.templateId ?? undefined,
        }}
      >
        <Form.Item label="Tên trên vé" name="displayName" rules={[{ max: 120, message: 'Tối đa 120 ký tự' }]}>
          <Input placeholder="Trống = lấy tên hiển thị của tài khoản" />
        </Form.Item>
        <Form.Item label="Nhóm khách" name="guestGroup" rules={[{ max: 32, message: 'Tối đa 32 ký tự' }]}>
          <AutoComplete
            options={groups.map((g) => ({ value: g }))}
            filterOption={groupFilter(groups)}
            allowClear
            placeholder="VD: KHÁCH MỜI"
          />
        </Form.Item>
        <Form.Item label="Ghi chú nội bộ" name="note" rules={[{ max: 300, message: 'Tối đa 300 ký tự' }]}>
          <TextArea rows={2} placeholder="Bàn, đi cùng…" />
        </Form.Item>
        <Form.Item label="Mẫu vé" name="templateId">
          <Select
            allowClear
            placeholder="Mặc định (theo nhóm rồi mẫu mặc định)"
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Gắn tài khoản
// ---------------------------------------------------------------------------

interface LinkProps {
  eventId: number;
  ticket: GalaTicketRow | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** Gắn tay một vé vào tài khoản app (không tự điền UID vào tài khoản). */
export const LinkTicketModal = ({ eventId, ticket, onClose, onSaved }: LinkProps) => {
  const { message } = App.useApp();
  const [picked, setPicked] = useState<PickOption | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ticket) setPicked(null);
  }, [ticket]);

  const submit = async () => {
    if (!ticket || !picked) return;
    setSaving(true);
    try {
      await linkTicket(eventId, ticket.id, Number(picked.value));
      message.success('Đã gắn tài khoản vào vé.');
      await onSaved();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không gắn được tài khoản.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={ticket ? `Gắn tài khoản — ${ticketLabel(ticket)}` : 'Gắn tài khoản'}
      open={!!ticket}
      onCancel={onClose}
      onOk={submit}
      okText="Gắn"
      cancelText="Huỷ"
      okButtonProps={{ disabled: !picked }}
      confirmLoading={saving}
      destroyOnClose
    >
      {ticket?.tiktokUid && <p className={styles.hint}>UID TikTok của vé: {ticket.tiktokUid}</p>}
      <DebounceSelect
        value={(picked ?? undefined) as any}
        fetchOptions={searchUserOptions}
        onChange={(v: any) => setPicked((v as PickOption) ?? null)}
        style={{ width: '100%' }}
        placeholder="Tìm theo tên, @username, email, UID…"
        aria-label="Tìm tài khoản để gắn"
      />
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Thu hồi (một hoặc nhiều vé)
// ---------------------------------------------------------------------------

interface RevokeProps {
  eventId: number;
  /** Các vé sẽ thu hồi; rỗng = đóng. */
  tickets: GalaTicketRow[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}

/** Thu hồi vé (bắt buộc lý do). Nhiều vé thì gọi lần lượt từng vé và báo vé nào lỗi. */
export const RevokeModal = ({ eventId, tickets, onClose, onDone }: RevokeProps) => {
  const { message } = App.useApp();
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);

  useEffect(() => {
    if (tickets.length) {
      setReason('');
      setFailed([]);
    }
  }, [tickets]);

  const submit = async () => {
    const why = reason.trim();
    if (!why) {
      message.warning('Hãy ghi lý do thu hồi.');
      return;
    }
    setRunning(true);
    const errors: string[] = [];
    let ok = 0;
    for (const t of tickets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await revokeTicket(eventId, t.id, why);
        ok += 1;
      } catch (e) {
        errors.push(`${ticketLabel(t)}: ${errorText(e, 'lỗi không rõ')}`);
      }
    }
    setRunning(false);
    await onDone();
    if (errors.length === 0) {
      message.success(`Đã thu hồi ${ok} vé.`);
      onClose();
    } else {
      setFailed(errors);
      message.warning(`Thu hồi được ${ok}/${tickets.length} vé.`);
    }
  };

  return (
    <Modal
      title={tickets.length === 1 ? `Thu hồi vé — ${ticketLabel(tickets[0])}` : `Thu hồi ${tickets.length} vé`}
      open={tickets.length > 0}
      onCancel={onClose}
      onOk={submit}
      okText="Thu hồi"
      okButtonProps={{ danger: true }}
      cancelText="Đóng"
      confirmLoading={running}
      destroyOnClose
    >
      <p className={styles.hint}>
        Vé bị thu hồi sẽ báo "Vé đã thu hồi" trong app và không quét vào cổng được. Bấm "Khôi phục" để cấp lại.
      </p>
      <TextArea
        rows={3}
        maxLength={300}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Lý do thu hồi (bắt buộc)"
        aria-label="Lý do thu hồi"
      />
      {failed.length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message="Một số vé chưa thu hồi được"
          description={failed.map((f) => (
            <div key={f}>{f}</div>
          ))}
        />
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Xem vé
// ---------------------------------------------------------------------------

interface ViewProps {
  ticket: GalaTicketRow | null;
  onClose: () => void;
}

/** Xem ảnh vé đã vẽ + tải về (PNG để in). */
export const ViewTicketModal = ({ ticket, onClose }: ViewProps) => {
  const png = ticket ? fileUrl(ticket.renderedImageKey || ticket.customImageKey) : '';
  const shown = ticket ? fileUrl(ticketThumbKey(ticket)) : '';
  return (
    <Modal
      title={ticket ? `Vé — ${ticketLabel(ticket)}` : 'Vé'}
      open={!!ticket}
      onCancel={onClose}
      footer={
        <>
          {png && (
            <Button icon={<DownloadOutlined />} href={png} target="_blank" rel="noreferrer" download>
              Tải ảnh vé
            </Button>
          )}
          <Button onClick={onClose}>Đóng</Button>
        </>
      }
      width={560}
      destroyOnClose
    >
      {ticket && shown ? (
        <img className={styles.ticketViewImg} src={shown} alt={`Vé của ${ticketLabel(ticket)}`} />
      ) : (
        <Alert type="info" showIcon message={'Vé này chưa có ảnh. Bấm "Vẽ lại" để vẽ.'} />
      )}
      {ticket && (
        <p className={styles.hint} style={{ marginTop: 8 }}>
          Mã tay: <strong>{ticket.shortCode}</strong>
          {ticket.renderError ? ` · Cảnh báo vẽ vé: ${ticket.renderError}` : ''}
        </p>
      )}
    </Modal>
  );
};
